import { format } from 'date-fns';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import DocumentHeader from '@/components/DocumentHeader';
import DocumentSchema from '@/components/DocumentSchema';
import type { NewsArticle, Distance } from 'schema-dts';
import type { Article } from '../hooks/useArticle';

import { helpers } from '@contentful/rich-text-types';
import type { Block } from '@contentful/rich-text-types';
import { PUBLIC_SWS_ENV } from '@/constants/env';

type Props = {
  article: Article;
};

// Note: This type predicate function is here to satisfy my refactor.
// TODO: Refactor all the Article work typing
const hasNodeType = (value: unknown): value is Block => {
  if (!value) {
    return false;
  }
  return typeof value === 'object' && 'nodeType' in value;
};

const ArticleMetadata = ({ article }: Props) => {
  const publishDate = format(new Date(article.created), 'MMMM dd, yyyy');
  const updatedDate = format(new Date(article.updated), 'MMMM dd, yyyy');
  return (
    <>
      <DocumentHeader
        allowCrawlers={PUBLIC_SWS_ENV === 'prod'}
        title={article.title}
        description={article.excerpt || ''}
      >
        <link
          rel="canonical"
          href={`https://simplywall.st${article.canonicalURL}`}
        />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@simplywallst" />
        <meta name="twitter:title" content={article.title} />
        {article.excerpt && (
          <meta name="twitter:description" content={article.excerpt} />
        )}
        <meta
          name="twitter:image"
          content={`https://simplywall.st/cdn-cgi/image/width=400,height=400,fit=crop,format=auto/${article.coverURL}`}
        />

        {article.expired && <meta name="robots" content="noindex, follow" />}
        <meta
          property="og:url"
          content={`https://simplywall.st${article.canonicalURL}`}
        />
        {article.coverURL && (
          <meta property="og:image" content={article.coverURL} />
        )}
        <meta name="iso-8601-publish-date" content={article.created} />
      </DocumentHeader>
      <DocumentSchema<NewsArticle>
        item={{
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          headline: article.title,
          dateModified: updatedDate,
          datePublished: publishDate,
          thumbnailUrl: article.coverURL || '',
          description: article.excerpt || '',
          url: `https://simplywall.st${article.canonicalURL}`,
          articleSection: 'Insights',
          isAccessibleForFree: true,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://simplywall.st${article.canonicalURL}`,
            lastReviewed: article.created || '',
            dateCreated: article.created || '',
            description: article.excerpt || '',
            reviewedBy: article.reviewedBy?.map((author) => ({
              '@type': 'Person',
              name: author.name,
              description:
                author.bio &&
                hasNodeType(author.bio) &&
                helpers.isBlock(author.bio)
                  ? documentToPlainTextString(author.bio)
                  : '',
              url: `https://simplywall.st/news/about-simply-wall-st-news/${author.slug}`,
            })),
          },
          image: {
            '@type': 'ImageObject',
            url: article.coverURL || '',
            thumbnailUrl: article.coverURL || '',
            width: '1200 px' as Distance,
            height: '411 px' as Distance,
          },
          author: [
            {
              '@type': 'Person',
              name: article.author!.name,
              url: `https://simplywall.st/news/about-simply-wall-st-news/${
                article.author!.slug
              }`,
              description:
                article?.author?.bio &&
                hasNodeType(article.author.bio) &&
                helpers.isBlock(article.author.bio)
                  ? documentToPlainTextString(article.author.bio)
                  : '',
            },
          ],
          publisher: {
            '@type': 'Organization',
            url: 'https://simplywall.st/',
            name: 'Simply Wall St',
            legalName: 'Simply Wall St',
            publishingPrinciples:
              'https://simplywall.st/news/about-simply-wall-st-news',
            logo: {
              '@type': 'ImageObject',
              url: 'https://simplywall.st/news/images/sws-logo.svg',
            },
          },
          inLanguage: 'en',
        }}
      />
    </>
  );
};

export default ArticleMetadata;
